/**
 * TerraMatrix Universal Data Platform - DataPage
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * Tabbed interface for data queries, sources, and admin management.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatContainer,
  QueryInput,
  SourcesPanel,
  HistoryPanel,
  SavedQueryChips,
  saveQueryToHistory,
  QueryResultsPanel,
  TemplatesPanel,
} from '@/components/data';
import { useDataQuery, ChatMessage } from '@/hooks/data/useDataQuery';
import type { QueryTemplate, TemplateExecutionResult } from '@/services/api/queryTemplateApi';
import { useDataSources } from '@/hooks/data/useDataSources';
import { DataSource } from '@/services/api/dataApi';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

export const DataPage: React.FC = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('query');

  // Panel visibility states (for Query tab)
  const [showSources, setShowSources] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ChatMessage | null>(null);
  const [lastUserQuery, setLastUserQuery] = useState<string>('');

  // Data hooks
  const { messages, isQuerying, sendQuery, clearMessages, addMessage } = useDataQuery();
  const { sources, isLoading: sourcesLoading, refetch: refetchSources } = useDataSources();

  // Auto-select the most recent assistant message with debug info
  useEffect(() => {
    const lastAssistantMessage = [...messages]
      .reverse()
      .find((m) => m.role === 'assistant' && m.debug && !m.isLoading);

    if (lastAssistantMessage) {
      setSelectedMessage(lastAssistantMessage);
      setShowResults(true);
    }
  }, [messages]);

  // Navigate to add new data source wizard
  const handleAddSource = useCallback(() => {
    navigate('/data/sources/new');
  }, [navigate]);

  // Navigate to data source detail page
  const handleViewSource = useCallback((source: DataSource) => {
    if (source.dataSourceId) {
      navigate(`/data/sources/${source.dataSourceId}`);
    }
  }, [navigate]);

  // Handle query submission with history tracking
  const handleSendQuery = useCallback(
    (query: string) => {
      saveQueryToHistory(query);
      setLastUserQuery(query);
      sendQuery(query);
    },
    [sendQuery]
  );

  // Handle follow-up suggestion clicks
  const handleFollowUpClick = useCallback(
    (suggestion: string) => {
      handleSendQuery(suggestion);
    },
    [handleSendQuery]
  );

  // Schema discovery - feature removed (was calling analytics-backend on port 4001)
  const handleDiscoverSchema = useCallback(
    async (source: DataSource) => {
      console.log('Schema discovery feature not available - analytics backend removed');
      alert('Schema discovery is temporarily unavailable.');
    },
    []
  );

  // Handle opening schema management modal - feature removed
  const handleManageSchema = useCallback((source: DataSource) => {
    console.log('Schema management feature not available - analytics backend removed');
    alert('Schema management is temporarily unavailable.');
  }, []);

  // Handle template execution results - display in Query tab
  const handleTemplateExecuted = useCallback(
    (result: TemplateExecutionResult, template: QueryTemplate) => {
      // Create user message showing what was executed
      const variablesSummary = Object.entries(result.metadata.resolvedVariables || {})
        .filter(([key]) => !key.startsWith('_')) // Skip system variables
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');

      const userMessage: ChatMessage = {
        id: `template-user-${Date.now()}`,
        role: 'user',
        content: `Executed template: ${template.name}${variablesSummary ? ` (${variablesSummary})` : ''}`,
        timestamp: new Date(),
      };

      // Create assistant message with results
      const assistantMessage: ChatMessage = {
        id: `template-result-${Date.now()}`,
        role: 'assistant',
        content: `Template "${template.name}" executed successfully. Found ${result.metadata.rowCount} rows in ${result.metadata.executionTimeMs}ms.`,
        timestamp: new Date(),
        data: result.data,
        source: {
          id: template.query?.schemaId || '',
          name: template.name,
        },
        debug: {
          executionTimeMs: result.metadata.executionTimeMs,
        },
      };

      // Add both messages
      addMessage(userMessage);
      addMessage(assistantMessage);

      // Switch to Query tab and show results
      setActiveTab('query');
      setShowResults(true);
      setSelectedMessage(assistantMessage);
    },
    [addMessage]
  );

  return (
    <>
      {/* Fixed viewport container - breaks out of RootLayout's min-h-screen */}
      <div className="fixed inset-0 top-[73px] z-10 bg-background flex flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header with Tabs - flex-shrink-0 so it doesn't collapse */}
          <div className="flex-shrink-0 border-b border-border">
            <div className="p-4 pb-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">Data</h1>
                </div>

                {/* Query tab controls - only show on query tab */}
                {activeTab === 'query' && (
                  <div className="flex items-center gap-2">
                    {/* Results Toggle */}
                    <button
                      onClick={() => setShowResults(!showResults)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                        showResults
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      }`}
                      aria-label="Toggle results panel"
                      aria-pressed={showResults}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                      <span className="text-sm">Results</span>
                    </button>

                    {/* Sources Toggle */}
                    <button
                      onClick={() => {
                        setShowSources(!showSources);
                        if (!showSources) setShowHistory(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                        showSources
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      }`}
                      aria-label="Toggle sources panel"
                      aria-pressed={showSources}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                        />
                      </svg>
                      <span className="text-sm">Sources ({sources?.length || 0})</span>
                    </button>

                    {/* History Toggle */}
                    <button
                      onClick={() => {
                        setShowHistory(!showHistory);
                        if (!showHistory) setShowSources(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                        showHistory
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      }`}
                      aria-label="Toggle history panel"
                      aria-pressed={showHistory}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm">History</span>
                    </button>

                    {/* Clear Chat */}
                    {messages.length > 1 && (
                      <button
                        onClick={clearMessages}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                        aria-label="Clear chat"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Tab Navigation */}
              <TabsList className="grid w-auto grid-cols-3 gap-1">
                <TabsTrigger value="query" className="px-6">
                  Query
                </TabsTrigger>
                <TabsTrigger value="templates" className="px-6">
                  Templates
                </TabsTrigger>
                <TabsTrigger value="sources" className="px-6">
                  Sources
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Query Tab Content - use calc to fill remaining space after header (~110px) */}
          <TabsContent value="query" className="mt-0 overflow-hidden" style={{ height: 'calc(100vh - 73px - 110px)' }}>
              {/* Main content area */}
              <div className="h-full flex overflow-hidden">
                {/* Left Column: Chat Interface */}
                <div className={`h-full flex flex-col min-w-0 overflow-hidden transition-all ${showResults ? 'w-1/2' : 'flex-1'}`}>
                  {/* Messages - scrollable */}
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <ChatContainer
                      messages={messages}
                      isQuerying={isQuerying}
                      onFollowUpClick={handleFollowUpClick}
                    />
                  </div>

                  {/* Input Area - fixed at bottom */}
                  <div className="flex-shrink-0 border-t border-border p-4 bg-background">
                    <SavedQueryChips onSelect={handleSendQuery} />
                    <QueryInput
                      onSubmit={handleSendQuery}
                      isLoading={isQuerying}
                      placeholder="Ask a question about your data..."
                    />
                    <div className="mt-2 text-xs text-muted-foreground text-center">
                      Press Enter to send • Shift+Enter for new line
                    </div>
                  </div>
                </div>

                {/* Right Column: Results Panel */}
                {showResults && (
                  <div className="w-1/2 min-h-0 border-l border-border bg-background overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex-shrink-0 border-b border-border p-4 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Query Results</h2>
                        <button
                          onClick={() => setShowResults(false)}
                          className="p-1 hover:bg-accent rounded-md transition-colors"
                          aria-label="Close results panel"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Results Content - scrollable */}
                    <div className="flex-1 min-h-0 overflow-y-auto">
                      <QueryResultsPanel
                        selectedMessage={selectedMessage}
                        onTemplateSaved={() => {
                          // Optionally switch to templates tab after saving
                          console.log('Template saved successfully');
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Side Panels (absolute overlays) */}
              {showSources && (
                <div className="absolute top-0 right-0 h-full z-20 shadow-xl">
                  <SourcesPanel
                    sources={sources}
                    isLoading={sourcesLoading}
                    onClose={() => setShowSources(false)}
                    onAddSource={handleAddSource}
                    onSelectSource={handleViewSource}
                    onDiscoverSchema={handleDiscoverSchema}
                    onManageSchema={handleManageSchema}
                    discoveringSourceId={null}
                  />
                </div>
              )}

              {showHistory && (
                <div className="absolute top-0 right-0 h-full z-20 shadow-xl">
                  <HistoryPanel
                    onSelect={handleSendQuery}
                    onClose={() => setShowHistory(false)}
                  />
                </div>
              )}
            </TabsContent>

            {/* Templates Tab Content */}
            <TabsContent value="templates" className="mt-0 data-[state=active]:flex data-[state=active]:flex-col data-[state=active]:flex-1 data-[state=active]:min-h-0 data-[state=active]:overflow-hidden">
              <TemplatesPanel
                className="flex-1 min-h-0"
                onTemplateExecuted={handleTemplateExecuted}
              />
            </TabsContent>

            {/* Sources Tab Content */}
            <TabsContent value="sources" className="mt-0 data-[state=active]:flex data-[state=active]:flex-col data-[state=active]:flex-1 data-[state=active]:min-h-0">
              <div className="flex-1 overflow-y-auto p-4">
                <div className="max-w-4xl mx-auto">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold mb-2">Data Sources</h2>
                    <p className="text-muted-foreground">
                      Manage your connected data sources for natural language queries.
                    </p>
                  </div>

                  <div className="flex justify-end mb-4">
                    <button
                      onClick={handleAddSource}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      + Add Source
                    </button>
                  </div>

                  {/* Sources List */}
                  {sourcesLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading sources...
                    </div>
                  ) : sources && sources.length > 0 ? (
                    <div className="space-y-4">
                      {sources.map((source) => (
                        <div
                          key={source.dataSourceId}
                          className="border border-border rounded-lg p-4 hover:bg-accent/50 cursor-pointer transition-colors"
                          onClick={() => handleViewSource(source)}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium">{source.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {source.type} • {source.status}
                              </p>
                              {source.metadata?.description && (
                                <p className="text-sm mt-1">{source.metadata.description}</p>
                              )}
                            </div>
                            <span
                              className={`px-2 py-1 text-xs rounded ${
                                source.status === 'active'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                              }`}
                            >
                              {source.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">📊</div>
                      <div className="text-lg font-medium">No data sources yet</div>
                      <div className="text-sm text-muted-foreground mb-4">
                        Add a data source to start querying your data.
                      </div>
                      <button
                        onClick={handleAddSource}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Add Your First Source
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

    </>
  );
};

export default DataPage;
